import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_FPS, DEFAULT_PLAYBACK_SPEED } from '@spine-workspace/shared';

export type PlaybackMode = 'loop' | 'once' | 'ping-pong';

interface PlaybackState {
  isPlaying: boolean;
  speed: number;
  currentFrame: number;
  totalFrames: number;
  fps: number;
  playbackMode: PlaybackMode;
  // Loop region (null = disabled)
  loopStart: number | null;
  loopEnd: number | null;
  // User preferences (persisted to localStorage)
  autoPlayOnLoad: boolean;
  play: () => void;
  pause: () => void;
  togglePlay: () => void;
  setSpeed: (speed: number) => void;
  seekToFrame: (frame: number) => void;
  stepForward: () => void;
  stepBackward: () => void;
  stepBy: (n: number) => void;
  setTotalFrames: (total: number) => void;
  setCurrentFrame: (frame: number) => void;
  setLoopStart: (frame: number | null) => void;
  setLoopEnd: (frame: number | null) => void;
  clearLoop: () => void;
  setPlaybackMode: (mode: PlaybackMode) => void;
  setAutoPlayOnLoad: (v: boolean) => void;
  // Global physics environment (live-only, not persisted to scene)
  globalWind: number;
  globalGravity: number;
  setGlobalWind: (wind: number) => void;
  setGlobalGravity: (gravity: number) => void;
}

export const usePlaybackStore = create<PlaybackState>()(
  persist(
    (set) => ({
      isPlaying: false,
      speed: DEFAULT_PLAYBACK_SPEED,
      currentFrame: 0,
      totalFrames: 0,
      fps: DEFAULT_FPS,
      playbackMode: 'loop' as PlaybackMode,
      loopStart: null,
      loopEnd: null,
      autoPlayOnLoad: false,
      globalWind: 0,
      globalGravity: 0,

      play: () => set({ isPlaying: true }),
      pause: () => set({ isPlaying: false }),
      togglePlay: () => set((s) => ({ isPlaying: !s.isPlaying })),
      setSpeed: (speed) => set({ speed }),
      seekToFrame: (frame) => set({ currentFrame: frame }),
      stepForward: () =>
        set((s) => ({ isPlaying: false, currentFrame: Math.min(s.currentFrame + 1, s.totalFrames) })),
      stepBackward: () =>
        set((s) => ({ isPlaying: false, currentFrame: Math.max(s.currentFrame - 1, 0) })),
      stepBy: (n) =>
        set((s) => ({ isPlaying: false, currentFrame: Math.max(0, Math.min(s.currentFrame + n, s.totalFrames)) })),
      setTotalFrames: (total) => set({ totalFrames: total }),
      setCurrentFrame: (frame) => set({ currentFrame: frame }),
      setLoopStart: (frame) => set({ loopStart: frame }),
      setLoopEnd: (frame) => set({ loopEnd: frame }),
      clearLoop: () => set({ loopStart: null, loopEnd: null }),
      setPlaybackMode: (mode) => set({ playbackMode: mode }),
      setAutoPlayOnLoad: (v) => set({ autoPlayOnLoad: v }),
      setGlobalWind: (wind) => set({ globalWind: wind }),
      setGlobalGravity: (gravity) => set({ globalGravity: gravity }),
    }),
    {
      name: 'spine-playback-prefs',
      // Only persist user preferences, not transient playback state
      partialize: (s) => ({
        autoPlayOnLoad: s.autoPlayOnLoad,
        speed: s.speed,
        fps: s.fps,
        playbackMode: s.playbackMode,
      }),
    }
  )
);
